/** @jsxImportSource @emotion/react */
import { useState, type ReactNode } from 'react';
import {
  panelCardStyle,
  panelHeaderStyle,
  panelTitleStyle,
  panelBodyStyle,
  collapseToggleStyle,
  chevronStyle,
} from './PanelCard.styles';

/** 색 계열. danger는 admin-mockup의 에러 박스에서 확인. */
export type PanelCardTone = 'default' | 'danger';

export interface PanelCardProps {
  /** 패널 제목. 무제목 패널(테이블 단독)은 생략 가능. */
  title?: ReactNode;
  /** 제목 우측 자리. 범례, 검색 컨트롤, 힌트 문구에 활용. (선택) */
  headerRight?: ReactNode;
  tone?: PanelCardTone;
  /** true면 본문 여백 제거. 테이블을 패널 가장자리까지 붙일 때 활용. */
  flush?: boolean;
  /** true면 제목 클릭으로 본문을 접을 수 있음. */
  collapsible?: boolean;
  /** collapsible일 때 초기 접힘 상태. */
  defaultCollapsed?: boolean;
  /** 패널 내용 자리. 차트·테이블·타일 등 무엇이든 수용. */
  children?: ReactNode;
}

/** 패널 카드. 대시보드의 차트·테이블·지표 묶음 컨테이너에 활용. */
export function PanelCard({
  title,
  headerRight,
  tone = 'default',
  flush = false,
  collapsible = false,
  defaultCollapsed = false,
  children,
}: PanelCardProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const hasHeader = title !== undefined || headerRight !== undefined || collapsible;
  const bodyHidden = collapsible && collapsed;

  return (
    <section css={panelCardStyle(tone)}>
      {hasHeader && (
        <div css={panelHeaderStyle(flush, bodyHidden)}>
          {collapsible ? (
            <button
              type="button"
              css={collapseToggleStyle}
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
            >
              <span css={chevronStyle(collapsed)}>▾</span>
              {title !== undefined && <h3 css={panelTitleStyle(tone)}>{title}</h3>}
            </button>
          ) : title !== undefined ? (
            <h3 css={panelTitleStyle(tone)}>{title}</h3>
          ) : (
            <span />
          )}
          {headerRight}
        </div>
      )}
      {!bodyHidden && <div css={panelBodyStyle(flush, hasHeader)}>{children}</div>}
    </section>
  );
}
